export const aiCourse = [
  {
    "metadata": {
      "id": "ai_explorers_week1_slides",
      "classTitle": "Artificial Intelligence",
      "keyStage": "",
      "order": 1,
      "module": "AI 101",
      "lesson": "Week 1",
      "yearGroup": "Year 1–2",
      "title": "What is AI?",
      "curriculumAlignment": "KS1 Computing: Recognise common uses of information technology beyond school. Understand that programs execute by following precise instructions.",
      "keyFocus": "Understanding AI in everyday life",
      "aiIntegration": "Sorting, chatbot conversations, role-play"
    },
    "slides": [
      {
        "title": "🤖 What’s a Machine?",
        "content": "A machine helps us do things.\nSome machines move.\nSome make noise.\nSome just follow buttons!",
        "image": "machine_examples_simple.jpg",
        "speakerNotes": {
          "objective": "Help students recognise everyday machines.",
          "questions": ["What machines do you use at home?", "Do they talk back or follow buttons?"],
          "imagePrompt": "Kettle, fan, washing machine, TV.",
          "scaffoldingTips": {
            "support": "Hold up toy or photo props.",
            "core": "Have students call out machines they know.",
            "challenge": "Ask: What’s one thing you wish a machine could do?"
          },
          "teacherTip": "Be playful — this sets the stage for bigger ideas."
        }
      },
      {
        "title": "💡 What Makes a Machine Smart?",
        "content": "Some machines just follow instructions.\nSome machines can learn!\nThat’s called being 'smart'.",
        "image": "smart_vs_following.jpg",
        "speakerNotes": {
          "objective": "Introduce the concept of smart vs basic machines.",
          "questions": ["Is a light switch smart?", "What about Alexa or a robot?"],
          "imagePrompt": "Two-column image: microwave vs smart assistant.",
          "scaffoldingTips": {
            "support": "Model with hand gestures: 'on/off' vs 'talking'.",
            "core": "Sort by yes/no together.",
            "challenge": "Ask: Can a machine ever get smarter over time?"
          },
          "teacherTip": "Use this to begin framing AI — without naming it yet."
        }
      },
      {
        "title": "📚 Mila and the Talking Toaster",
        "content": "Mila was having breakfast.\nShe said, 'Tell me a joke!'\nThe toaster just popped her toast.\nMila wondered: Can a toaster talk?",
        "image": "mila_toaster_story.jpg",
        "speakerNotes": {
          "objective": "Use story to blend imagination and real-world logic.",
          "questions": ["What did Mila expect?", "Can your toaster talk?"],
          "imagePrompt": "Girl talking to toaster with speech bubble.",
          "scaffoldingTips": {
            "support": "Tell the story with expressions or puppets.",
            "core": "Read aloud with dramatic tone.",
            "challenge": "Ask: What would it take for a toaster to talk back?"
          },
          "teacherTip": "Lean into the imagination — let them wonder."
        }
      },
      {
        "title": "🧠 What is AI?",
        "content": "AI means Artificial Intelligence.\nThat’s when a machine learns to do smart things.\nLike talk, understand pictures, or make choices!",
        "image": "ai_definition_icons.jpg",
        "speakerNotes": {
          "objective": "Give students a first definition of AI.",
          "questions": ["Can a machine really learn?"],
          "imagePrompt": "Icons of brain, robot, and data patterns.",
          "scaffoldingTips": {
            "support": "Say: 'AI means learning helpers.'",
            "core": "Repeat and chant: 'AI = learning machines!'",
            "challenge": "Ask: Where have you seen AI before?"
          },
          "teacherTip": "Don’t overexplain — just establish a link between 'smart' and 'learning'."
        }
      },
      {
        "title": "🧩 Smart or Not?",
        "content": "Let’s sort some machines.\nDo they follow instructions, or learn new things?",
        "image": "sorting_smart_game.jpg",
        "speakerNotes": {
          "objective": "Reinforce smart vs basic machine distinction.",
          "questions": ["Is this smart or not?"],
          "imagePrompt": "Flash cards: light switch, microwave, chatbot, robot.",
          "scaffoldingTips": {
            "support": "Use physical cards or objects.",
            "core": "Sort on a whiteboard or table.",
            "challenge": "Ask: What would make this 'not smart' machine smarter?"
          },
          "teacherTip": "Let students disagree — great thinking comes from debate."
        }
      },
      {
        "title": "💬 Ask the Bot!",
        "content": "Let’s ask a smart machine something fun!\nWhat happens when we say: 'Tell me a joke about school!'?",
        "image": "chatbot_demo_kids.jpg",
        "speakerNotes": {
          "objective": "Model chatbot interaction for the class.",
          "questions": ["Was the answer silly or smart?", "Could a toaster do that?"],
          "aiPrompt": "Tell me a joke about school!",
          "imagePrompt": "Digital assistant on screen responding to a child.",
          "scaffoldingTips": {
            "support": "Use printed examples if tech isn’t available.",
            "core": "Read response aloud — then ask reactions.",
            "challenge": "Ask: Could the bot learn from our jokes too?"
          },
          "teacherTip": "Use AIDEN’s GPT or ChatGPT. Remind students this is just a tool, not a person."
        }
      },
      {
        "title": "🎨 Draw Your Smart Machine!",
        "content": "What does your smart machine do?\nHow does it help?\nCan it learn or talk?",
        "image": "draw_your_ai_machine.jpg",
        "speakerNotes": {
          "objective": "Encourage creative thinking and AI application.",
          "questions": ["What job does your machine do?", "Does it learn something new?"],
          "imagePrompt": "Worksheet with drawing box and sentence stem.",
          "scaffoldingTips": {
            "support": "Provide categories: school, home, space.",
            "core": "Use sentence starter: 'My smart machine can…'",
            "challenge": "Ask: What data would your machine need to learn?"
          },
          "teacherTip": "All designs are valid — focus on ideas over neatness."
        }
      },
      {
        "title": "🎤 Let’s Share!",
        "content": "Show your drawing!\nWhat makes your machine smart?",
        "image": "kids_presenting_ai.jpg",
        "speakerNotes": {
          "objective": "Encourage voice and idea-sharing.",
          "questions": ["What do you like about their machine?"],
          "imagePrompt": "Kids presenting with big smiles and colorful drawings.",
          "scaffoldingTips": {
            "support": "Ask yes/no prompts.",
            "core": "Have each student say one thing their machine does.",
            "challenge": "Ask: Could this machine get even smarter next year?"
          },
          "teacherTip": "This is celebration time — affirm creative risk-taking."
        }
      },
      {
        "title": "💬 Let’s Think About It…",
        "content": "- What makes a machine smart?\n- What surprised you today?\n- If you made a robot, what would it do?",
        "image": "student_reflection_minds.jpg",
        "speakerNotes": {
          "objective": "Build early metacognitive habits.",
          "questions": ["What did you learn about smart machines?", "Would you trust a robot to help you?"],
          "imagePrompt": "Child thinking with spark and speech bubbles.",
          "scaffoldingTips": {
            "support": "Say: 'Finish this sentence…'",
            "core": "Turn and talk, then share one idea.",
            "challenge": "Ask: Could a machine ever be smarter than a person?"
          },
          "teacherTip": "Use this space to deepen thinking and curiosity."
        }
      }
    ],

    "teacherMaterials": [
      {
        "name": "Lesson Plan & Objectives",
        "componentType": "lessonplanObjectives",
        "content": [
          {
            "sectionName": "Learning Objectives",
            "title": "What Students Will Learn",
            "description": "By the end of this lesson, students will: ✅ Understand that some machines just follow instructions, while others can learn or respond✅ Be introduced to the idea of Artificial Intelligence(AI)✅ Explore AI in a playful and imaginative way✅ Design their own 'smart machine'✅ Begin to reflect on what makes something 'smart'",
            "icon": "TargetIcon"
          }
        ],
        "lessonPlan": {
          "sections": [
            {
              "part": 1,
              "name": "What’s a Machine?",
              "duration": 5,
              "activities": [
                {
                  "icon": "DiscussionIcon",
                  "primary": "Ask: 'What machines do you use at home?'",
                  "secondary": "Discuss what they do — do they talk back, or follow buttons?"
                }
              ]
            },
            {
              "part": 2,
              "name": "What Makes a Machine Smart?",
              "duration": 5,
              "activities": [
                {
                  "icon": "CompareIcon",
                  "primary": "Compare: smart vs simple machines (microwave vs chatbot).",
                  "secondary": "Use pictures or icons for visual clarity."
                }
              ]
            },
            {
              "part": 3,
              "name": "Mila and the Talking Toaster",
              "duration": 7,
              "activities": [
                {
                  "icon": "StoryIcon",
                  "primary": "Read the story aloud with voice and expression.",
                  "secondary": "Discuss: Why didn’t the toaster answer?"
                }
              ]
            },
            {
              "part": 4,
              "name": "What is AI?",
              "duration": 5,
              "activities": [
                {
                  "icon": "AIIcon",
                  "primary": "Explain: AI = machines that learn from patterns, not just follow rules.",
                  "secondary": "Use icons to reinforce: talking, recognising, choosing."
                }
              ]
            },
            {
              "part": 5,
              "name": "Smart or Not?",
              "duration": 5,
              "activities": [
                {
                  "icon": "SortIcon",
                  "primary": "Use images to sort machines into smart vs not smart.",
                  "secondary": "Ask: Why is it smart? What can it do?"
                }
              ]
            },
            {
              "part": 6,
              "name": "Ask the Bot!",
              "duration": 5,
              "activities": [
                {
                  "icon": "ChatIcon",
                  "primary": "Use AIDEN GPT or ChatGPT to ask: 'Tell me a joke about school!'",
                  "secondary": "Discuss: Was the response smart or silly? Could a microwave do that?"
                }
              ]
            },
            {
              "part": 7,
              "name": "Draw Your Smart Machine!",
              "duration": 7,
              "activities": [
                {
                  "icon": "DrawIcon",
                  "primary": "Students draw a smart machine and describe what it does.",
                  "secondary": "Prompt: 'My smart machine can…'"
                }
              ]
            },
            {
              "part": 8,
              "name": "Let’s Share!",
              "duration": 6,
              "activities": [
                {
                  "icon": "PresentationIcon",
                  "primary": "A few students present their smart machines to the class.",
                  "secondary": "Others give feedback: thumbs up, what they liked."
                }
              ]
            },
            {
              "part": 9,
              "name": "Reflection",
              "duration": 5,
              "activities": [
                {
                  "icon": "ReflectionIcon",
                  "primary": "Ask metacognitive prompts: 'What makes a machine smart?', 'What surprised you?'",
                  "secondary": "Turn and talk or share aloud."
                }
              ]
            }
          ]
        }
      },
      {
        "name": "Assessment & Homework",
        "componentType": "assessment",
        "content": [
          {
            "sectionName": "Assessment Criteria",
            "title": "Evaluating Student Understanding",
            "description": "✔️ Can identify smart vs non-smart technology ✔️ Participated in chatbot activity and discussion ✔️ Created and described an original smart machine ✔️ Reflected on their thinking using guided prompts",
            "icon": "CheckIcon"
          },
          {
            "sectionName": "Homework Task",
            "title": "Smart Tech at Home",
            "description": "Ask someone at home: 'Do we have any smart machines?'Draw one and describe what it does.",
            "icon": "BookIcon"
          }
        ]
      }
    ]
  },
  {
    "metadata": {
      "id": "ai_explorers_week1_y34",
      "classTitle": "Artificial Intelligence",
      "keyStage": "",
      "order": 1,
      "module": "AI 101",
      "lesson": "Week 1",
      "yearGroup": "Year 3–4",
      "title": "What is AI?",
      "curriculumAlignment": "KS2 Computing: Use logical reasoning to understand how algorithms work; recognise technology’s role in everyday life.",
      "keyFocus": "Understanding AI in everyday life",
      "aiIntegration": "Sorting game, chatbot demo (GPT), reflection with AI comparison"
    },
    "slides": [
      {
        "title": "🧠 A Brief History of AI",
        "content": "People have dreamed about smart machines for a long time.The word 'Artificial Intelligence' was first used in 1956.Since then, AI has been used to play chess, understand speech, and drive cars!",
        "image": "timeline_ai_history.jpg",
        "speakerNotes": {
          "objective": "Give students a sense of the origin and evolution of AI.",
          "questions": ["Do you think people were using AI before 1956?", "What might AI do in the future?"],
          "imagePrompt": "Timeline or collage showing chess computer, voice assistant, self-driving car.",
          "scaffoldingTips": {
            "support": "Name a few jobs AI helps with.",
            "core": "Read together and ask for reactions.",
            "challenge": "Ask: Why do you think people wanted machines to learn?"
          },
          "teacherTip": "Keep this short and engaging — it’s a launchpad, not a history lecture."
        }
      },
      {
        "title": "❓Can AI Be Wrong?",
        "content": "AI can guess wrong!Sometimes it sees a cat and says it’s a dog.It’s not magic — it learns from examples, and it makes mistakes.",
        "image": "ai_mistakes_cat_dog.jpg",
        "speakerNotes": {
          "objective": "Introduce the idea that AI can make errors based on patterns — not understanding.",
          "questions": ["Have you ever been confused like that?", "Should we always trust AI?"],
          "imagePrompt": "Funny AI mislabelled image: cat labelled as toaster or dog.",
          "scaffoldingTips": {
            "support": "Tell a story of an AI misunderstanding.",
            "core": "Ask: What clues do *you* use to know a cat is a cat?",
            "challenge": "Ask: How can we help AI make better guesses?"
          },
          "teacherTip": "This is a great place to reinforce critical thinking — AI is helpful, but not perfect."
        }
      },
      {
        "title": "🌍 Where Do You See AI Today?",
        "content": "AI is all around us — in: - Phones (voice assistants) - YouTube (video suggestions) - Video games - Smart cameras Which ones have you seen?",
        "image": "ai_everyday_examples.jpg",
        "speakerNotes": {
          "objective": "Help students connect classroom concepts to their daily lives.",
          "questions": ["Who has used a voice assistant?", "Have you seen a recommendation online?"],
          "imagePrompt": "Grid of icons: phone, smart speaker, camera, video game.",
          "scaffoldingTips": {
            "support": "Use real items or show a live example.",
            "core": "Create a tally chart of tools students have used.",
            "challenge": "Ask: What kind of data do these AIs use?"
          },
          "teacherTip": "Use this to spark relevance — students already live with AI."
        }
      },
      {
        "title": "🧠 Smart or Simple Machine?",
        "content": "Some machines just follow instructions.Some can change or respond.Let’s look at a few and decide together.",
        "image": "smart_vs_simple_icons.jpg",
        "speakerNotes": {
          "objective": "Help students distinguish between fixed-program and adaptive machines.",
          "questions": ["Which of these machines do you think can learn?", "How do we know if a machine is 'smart'?"],
          "imagePrompt": "Use icon-style visuals for toaster, traffic light, robot vacuum, chatbot.",
          "scaffoldingTips": {
            "support": "Give clear examples aloud to model thinking.",
            "core": "Thumbs up for smart, thumbs down for simple. Discuss in pairs.",
            "challenge": "Ask: Could a simple machine become smart with upgrades?"
          },
        },
        "teacherTip": "Keep it energetic and visual — students will love making predictions."
      },
      {
        "title": "😂 Story: The Joke Challenge",
        "content": "A child asks: 'Can you tell me a joke?'\nMicrowave says: 'ERROR'.\nChatbot tells a funny joke!\n→ Who was smarter? Why?",
        "image": "microwave_vs_chatbot_comic.jpg",
        "speakerNotes": {
          "objective": "Use humour to explore how AI behaves differently from programmed machines.",
          "questions": ["Why could the chatbot tell a joke?", "What does that mean about how it works?"],
          "imagePrompt": "Comic strip with child, microwave, chatbot.",
          "scaffoldingTips": {
            "support": "Act it out with props.",
            "core": "Project comic and pause after each panel.",
            "challenge": "Ask: Did the chatbot really understand the joke?"
          },
          "teacherTip": "Use this as a launchpad for the next sorting activity."
        }
      },
      {
        "title": "🧠 What is AI?",
        "content": "AI stands for Artificial Intelligence.\nBut guess what? Even scientists don’t agree on one definition!\nSome say: It’s when machines learn from data.\nSome say: It’s when machines do things that feel smart.\nLet’s keep it simple: AI = machines that learn to do smart things!",
        "image": "ai_definition_concepts.jpg",
        "speakerNotes": {
          "objective": "Introduce the uncertainty and diversity of AI definitions.",
          "questions": ["Which definition makes the most sense to you?"],
          "imagePrompt": "Icons showing brain, puzzle, computer.",
          "scaffoldingTips": {
            "support": "Read aloud each sentence slowly.",
            "core": "Use echo-reading or pair-sharing.",
            "challenge": "Ask: Can we make our own definition of AI?"
          },
          "teacherTip": "Let them know — there’s no one right answer yet!"
        }
      },
      {
        "title": "🧩 Group Challenge: Smart or Not?",
        "content": "Let’s sort some tech tools.\nWhich ones follow rules?\nWhich ones adapt or talk back?",
        "image": "group_sort_cards.jpg",
        "speakerNotes": {
          "objective": "Practice classifying technology as rule-based or adaptive.",
          "questions": ["What makes this tool smart?"],
          "imagePrompt": "Cards: calculator, Alexa, game console, fan, camera.",
          "scaffoldingTips": {
            "support": "Give 1 example in each category.",
            "core": "Students work in pairs with printed cards.",
            "challenge": "Justify their reasoning to another group."
          },
          "teacherTip": "Prompt with: 'Tell me why you chose that group.'"
        }
      },
      {
        "title": "💬 Ask the Bot (Demo Time!)",
        "content": "Let’s try asking two questions:\n1. What is 5 + 5?\n2. Tell me a pizza joke!\n→ Did the answers feel different?",
        "image": "ai_gpt_demo_pizza.jpg",
        "speakerNotes": {
          "objective": "Demonstrate how AI can give both factual and creative answers.",
          "questions": ["Was the joke creative?", "Could a fridge do that?"],
          "aiPrompt": ["What is 5 + 5?", "Tell me a pizza joke!"],
          "imagePrompt": "Child laughing at chatbot answer on screen.",
          "scaffoldingTips": {
            "support": "Model each answer and show both on board.",
            "core": "Thumbs up if they felt different.",
            "challenge": "Ask: How did the bot know what was funny?"
          },
          "teacherTip": "Use AIDEN GPT if available, or a prepared slide if not."
        }
      },
      {
        "title": "📝 Think, Pair, Bot, Share",
        "content": "1. Write: 'What makes something smart?'\n2. Ask the bot the same question.\n3. Compare: What was the same? What was different?",
        "image": "think_pair_bot_share.jpg",
        "speakerNotes": {
          "objective": "Encourage metacognition by comparing human and AI answers.",
          "questions": ["Did the AI say it like you?", "What did it miss?"],
          "imagePrompt": "Student thinking cloud vs chatbot bubble.",
          "scaffoldingTips": {
            "support": "Use sentence starter: 'Something smart is…'",
            "core": "Turn and talk after comparison.",
            "challenge": "Ask: Which answer was more thoughtful — yours or the bot’s?"
          },
          "teacherTip": "Highlight how both human and machine answers are valuable — for different reasons."
        }
      },
      {
        "title": "💡 Let’s Reflect Together",
        "content": "- Can machines really think?\n- What surprised you today?\n- What would *your* AI tool do if it could learn?",
        "image": "student_metacognition_thinking.jpg",
        "speakerNotes": {
          "objective": "Close the lesson with meaningful reflection.",
          "questions": ["Can a machine ever be creative?", "Would you want a robot friend?"],
          "imagePrompt": "Kids sitting in a circle with idea bubbles.",
          "scaffoldingTips": {
            "support": "Say: 'One thing I learned is…'",
            "core": "Let each pair share one thought.",
            "challenge": "Ask: What else would you want AI to do for the world?"
          },
          "teacherTip": "Use this time to build wonder — this is just the beginning of their AI journey."
        }
      },
      {
        "title": "📘 Homework: Smart Tech at Home",
        "content": "Ask someone at home: 'Have we ever used something smart like Alexa or a chatbot?'\nDraw or describe it.\n➡️ Bring it in next class and tell us what made it smart.",
        "image": "home_ai_drawing_task.jpg",
        "speakerNotes": {
          "objective": "Bridge school and home experiences of AI.",
          "questions": ["Have you ever seen AI at home or on a phone?"],
          "imagePrompt": "Child holding a picture of a smart speaker.",
          "scaffoldingTips": {
            "support": "Give sentence starter: 'This tool is smart because...'",
            "core": "Have students draw it on the back of their worksheet.",
            "challenge": "Ask them to imagine how it could be smarter!"
          },
          "teacherTip": "Encourage family conversations — this makes AI relevant outside the classroom."
        }
      }
    ],
    "teacherMaterials": [
      {
        "name": "Lesson Plan & Objectives",
        "componentType": "lessonplanObjectives",
        "content": [
          {
            "sectionName": "Learning Objectives",
            "title": "What Students Will Learn",
            "description": "By the end of this lesson, students will:\n✅ Understand that AI is a type of smart technology that learns from data.\n✅ Distinguish between machines that follow rules and those that respond adaptively.\n✅ Explore multiple definitions of AI and reflect on which one makes the most sense.\n✅ Compare their own thinking with how AI responds.\n✅ Reflect on how AI appears smart without actually understanding.",
            "icon": "TargetIcon"
          }
        ],
        "lessonPlan": {
          "sections": [
            {
              "part": 1,
              "name": "Starter Activity: Smart or Simple?",
              "duration": 10,
              "activities": [
                {
                  "icon": "GroupIcon",
                  "primary": "Show a range of machines (e.g., toaster, robot vacuum, chatbot).",
                  "secondary": "Students decide: is it a rule-follower or a learning machine? Vote with movement or gestures."
                },
                {
                  "icon": "TimelineIcon",
                  "primary": "Introduce a short history of AI with images.",
                  "secondary": "Explain that AI started around 1956 and now helps in games, speech, and self-driving cars."
                },
                {
                  "icon": "DiscussionIcon",
                  "primary": "Discuss: 'Why do people want machines that can learn?'",
                  "secondary": "Encourage speculation and wonder."
                }
              ]
            },
            {
              "part": 2,
              "name": "Main Activity: Defining & Sorting AI",
              "duration": 15,
              "activities": [
                {
                  "icon": "StoryIcon",
                  "primary": "Tell the story of a child asking both a microwave and chatbot to tell a joke.",
                  "secondary": "Microwave fails, chatbot gives a joke — students decide why one could and one couldn’t."
                },
                {
                  "icon": "AIIcon",
                  "primary": "Show three simple definitions of AI and explain that experts still disagree.",
                  "secondary": "Ask: 'Which definition makes the most sense to you?'"
                },
                {
                  "icon": "SortIcon",
                  "primary": "Students classify tools as rule-based or AI-powered.",
                  "secondary": "Work in pairs with cards or slide images. Justify at least one choice."
                }
              ]
            },
            {
              "part": 3,
              "name": "Practice Exercise: Comparing Human vs AI Thinking",
              "duration": 15,
              "activities": [
                {
                  "icon": "RobotIcon",
                  "primary": "Use AIDEN GPT to model two prompts: 'What is 5 + 5?' and 'Tell me a pizza joke.'",
                  "secondary": "Compare the factual vs creative answers."
                },
                {
                  "icon": "WritingIcon",
                  "primary": "Students write their own answer to: 'What makes something smart?'",
                  "secondary": "Then ask the AI the same thing and compare. What was the same? What was different?"
                },
                {
                  "icon": "DiscussionIcon",
                  "primary": "Share out reflections: What surprised you about the AI’s answer?",
                  "secondary": "Use sentence starters or group support if needed."
                }
              ]
            },
            {
              "part": 4,
              "name": "Plenary: Metacognitive Reflection & Discussion",
              "duration": 5,
              "activities": [
                {
                  "icon": "DiscussionIcon",
                  "primary": "Discuss: 'Can a machine really think?' and 'What would your smart machine need to learn?'",
                  "secondary": "Encourage students to dream up a use for AI in their lives."
                }
              ]
            }
          ]
        }
      },
      {
        "name": "Assessment & Homework",
        "componentType": "assessment",
        "content": [
          {
            "sectionName": "Assessment Criteria",
            "title": "Evaluating AI Understanding",
            "description": "Students will be assessed on their ability to:\n✔️ Identify rule-following vs adaptive machines.\n✔️ Explain how AI learns from data and patterns.\n✔️ Compare their thinking to an AI-generated response.\n✔️ Reflect on how AI behaves versus how humans think.",
            "icon": "CheckIcon"
          },
          {
            "sectionName": "Homework Task",
            "title": "AI at Home",
            "description": "Ask someone at home: 'Have you ever used something smart like Alexa or a chatbot?'\nDraw or describe it. Bring it in and explain what made it smart.",
            "icon": "BookIcon"
          }
        ]
      }
    ]
  },
  {
    "metadata": {
      "id": "ai_explorers_week1_y56",
      "classTitle": "Artificial Intelligence",
      "keyStage": "",
      "order": 1,
      "module": "AI 101",
      "lesson": "Week 1",
      "yearGroup": "Year 5–6",
      "title": "What is AI?",
      "curriculumAlignment": "KS2 Computing: Understand computer networks and digital systems, logical reasoning, pattern recognition, and safe use of technology.",
      "keyFocus": "Understanding how AI uses data to act and improve",
      "aiIntegration": "Live GPT demo, rule-based vs adaptive sorting, definition comparison"
    },
    "slides": [
      {
        "title": "📜 A Brief History of AI",
        "content": "AI isn’t brand new — people have dreamed of smart machines for decades! \n - 1950: Alan Turing asked, 'Can machines think?' \n - 1956: The term 'Artificial Intelligence' was born \n - Now: AI helps in games, phones, cars, hospitals!",
        "image": "ai_timeline_kids.jpg",
        "speakerNotes": {
          "objective": "Give students a sense of AI’s evolution and growing presence.",
          "questions": ["Which example surprised you most?", "What might AI do in the future?"],
          "imagePrompt": "Timeline with vintage computer, 90s robot toy, and modern AI tools.",
          "scaffoldingTips": {
            "support": "Use real-life visuals to anchor the timeline.",
            "core": "Read each milestone aloud and pause for reactions.",
            "challenge": "Ask: What might AI do in 2050?"
          },
          "teacherTip": "Frame AI as something humans have been imagining for a long time — it's not magic, it's history in motion."
        }
      },
      {
        "title": "🎭 The Turing Test",
        "content": "Alan Turing asked: 'If a machine could answer like a human, would you know it’s a machine?' This idea became the 'Turing Test.' Can YOU spot the bot in a chat?",
        "image": "turing_test_game.jpg",
        "speakerNotes": {
          "objective": "Introduce one of the foundational ideas in AI in a fun, simple way.",
          "questions": ["Could a machine ever trick you?", "What clues would give it away?"],
          "imagePrompt": "Two chat bubbles — one human, one AI — students guess which is which.",
          "scaffoldingTips": {
            "support": "Give clear examples of machine answers.",
            "core": "Run a live or mock Turing Test as a group.",
            "challenge": "Ask: If a machine passes the test, is it thinking?"
          },
          "teacherTip": "Use this slide to spark philosophical discussion — this idea is still debated today!"
        }
      },
      {
        "title": "🤖 Can Machines Think?",
        "content": "Let’s start with a big question:\nCan machines actually think?\nThink, talk, and share your ideas.",
        "image": "machine_thinking_question.jpg",
        "speakerNotes": {
          "objective": "Stimulate inquiry and discussion from the outset.",
          "questions": ["What does it mean to think?", "Do calculators think?"],
          "imagePrompt": "AI robot and child both looking at a lightbulb icon.",
          "scaffoldingTips": {
            "support": "Use sentence starters like: 'I think machines... because...'",
            "core": "Let pairs discuss for 1 minute, then share",
            "challenge": "Ask: Can machines ever have feelings or ideas of their own?"
          },
          "teacherTip": "Emphasise: There are no wrong answers — just big thinking."
        }
      },
      {
        "title": "🧠 What is AI? Definitions & Debate",
        "content": "AI means Artificial Intelligence.\nBut here’s the thing — even experts don’t agree on one definition!\nSome say: 'AI is when machines learn from data.'\nOthers say: 'AI is when machines do things that seem smart.'\nLet’s pick which makes the most sense — or make our own!",
        "image": "ai_definition_multiple.jpg",
        "speakerNotes": {
          "objective": "Introduce AI as a flexible and evolving concept.",
          "questions": ["Which definition makes the most sense to you?", "Would you change anything?"],
          "imagePrompt": "Definitions displayed as puzzle pieces.",
          "scaffoldingTips": {
            "support": "Highlight key words like 'learn' and 'data'.",
            "core": "Read each definition aloud and vote.",
            "challenge": "Ask: What would your own definition be?"
          },
          "teacherTip": "Help students realise that defining AI is part of the learning journey."
        }
      },
      {
        "title": "💬 Ask the AI (Demo Time!)",
        "content": "Let’s ask two questions:\n1. What is 5 + 5?\n2. Tell me a pizza joke!\n→ How do the answers feel different?",
        "image": "ai_demo_pizza_math.jpg",
        "speakerNotes": {
          "objective": "Show contrast between rule-following vs pattern-based generation.",
          "questions": ["Did the AI just give facts, or did it create?"],
          "aiPrompt": ["What is 5 + 5?", "Tell me a pizza joke!"],
          "imagePrompt": "Chatbot screen with math answer and joke side by side.",
          "scaffoldingTips": {
            "support": "Repeat each output and explain.",
            "core": "Ask: Which one was more surprising?",
            "challenge": "Ask: What kind of thinking did the AI use in each case?"
          },
          "teacherTip": "You can use AIDEN GPT or ChatGPT — or a screenshot if no live demo is available."
        }
      },
      {
        "title": "🔍 Rule or Learner? Sorting Challenge",
        "content": "Let’s look at these machines:\nDo they follow strict rules?\nOr do they learn from data?\n→ Sort and explain your thinking.",
        "image": "tech_sorting_cards.jpg",
        "speakerNotes": {
          "objective": "Help students identify traits of AI and rule-based tools.",
          "questions": ["Why did you put it in that group?"],
          "imagePrompt": "Card visuals: calculator, Alexa, video game NPC, camera, robot vacuum.",
          "scaffoldingTips": {
            "support": "Model one example together.",
            "core": "Work in pairs. Justify at least one item aloud.",
            "challenge": "Add a new tool from home and place it."
          },
          "teacherTip": "Use sticky notes or digital tools if working in groups."
        }
      },
      {
        "title": "📝 Human vs AI: What Is Smart?",
        "content": "Write your own answer to:\n'What makes something smart?'\nThen ask the AI the same question.\n→ What did it get right? What did it miss?",
        "image": "human_vs_ai_thinking.jpg",
        "speakerNotes": {
          "objective": "Compare emotional reasoning with pattern-driven answers.",
          "questions": ["Did the AI include feelings? Context?"],
          "imagePrompt": "Split-screen: student thought bubble vs chatbot answer.",
          "scaffoldingTips": {
            "support": "Use sentence starter: 'Smart means…'",
            "core": "Compare and share with a partner.",
            "challenge": "Ask: Could AI ever become truly smart?"
          },
          "teacherTip": "Help students appreciate what human thinking adds."
        }
      },
      {
        "title": "💬 Let’s Reflect & Wonder",
        "content": "What did we learn about AI?\nWhat surprised you?\nWhat questions do you still have?\n→ Share one takeaway and one 'I wonder…' question.",
        "image": "reflection_circle_thinking.jpg",
        "speakerNotes": {
          "objective": "Encourage metacognition and inquiry.",
          "questions": ["What do you want to learn more about?", "Where do you see AI in your life?"],
          "imagePrompt": "Students sitting in circle sharing thoughts.",
          "scaffoldingTips": {
            "support": "Provide sentence starters: 'Today I learned…', 'I wonder…'",
            "core": "Let each table nominate a rep to share.",
            "challenge": "Ask: Could your question be answered by a human — or an AI?"
          },
          "teacherTip": "Celebrate curiosity — that’s the spirit of AI learning."
        }
      },
      {
        "title": "📘 Homework: AI at Home",
        "content": "Ask someone at home:\n‘Do we have any smart machines like Alexa, a robot vacuum, or YouTube suggestions?’\nDraw or explain how it helps.\n→ Share in next class.",
        "image": "ai_homework_draw_and_tell.jpg",
        "speakerNotes": {
          "objective": "Reinforce learning through home connection.",
          "questions": ["How does the tool help your family?", "Does it learn or just follow?"],
          "imagePrompt": "Child holding picture of a smart speaker or game.",
          "scaffoldingTips": {
            "support": "Students can describe instead of drawing.",
            "core": "Bring in example to present.",
            "challenge": "Add: 'How could this tool become even smarter?'"
          },
          "teacherTip": "This keeps the conversation going between school and home."
        }
      }
    ],
    "teacherMaterials": [
      {
        "name": "Lesson Plan & Objectives",
        "componentType": "lessonplanObjectives",
        "content": [
          {
            "sectionName": "Learning Objectives",
            "title": "What Students Will Learn",
            "description": "By the end of this lesson, students will:\n✅ Define AI as technology that learns from data and makes predictions.\n✅ Identify rule-based vs AI systems using real-world examples.\n✅ Explore multiple definitions of AI and reflect on which is most meaningful.\n✅ Understand the difference between AI-generated and human thinking.\n✅ Engage in ethical and critical thinking about AI’s limits and possibilities.",
            "icon": "TargetIcon"
          }
        ],
        "lessonPlan": {
          "sections": [
            {
              "part": 1,
              "name": "Starter Activity: Can Machines Think?",
              "duration": 8,
              "activities": [
                {
                  "icon": "GroupIcon",
                  "primary": "Pose the question: 'Can a machine think?' and have students discuss in pairs.",
                  "secondary": "Facilitate a short share-out. Emphasise this is a thinking launch — not a quiz."
                },
                {
                  "icon": "TimelineIcon",
                  "primary": "Introduce a brief history of AI using a visual timeline.",
                  "secondary": "Cover milestones like the Turing Test and AI in games and speech tools."
                }
              ]
            },
            {
              "part": 2,
              "name": "Main Activity: What is AI?",
              "duration": 15,
              "activities": [
                {
                  "icon": "PuzzleIcon",
                  "primary": "Share 2–3 definitions of AI from experts.",
                  "secondary": "Students vote on which they find most clear or useful. Discuss why they chose it."
                },
                {
                  "icon": "GameIcon",
                  "primary": "Introduce the Turing Test using a fun game slide.",
                  "secondary": "Ask students: 'Which message is the human? Which is the AI?'"
                },
                {
                  "icon": "RobotIcon",
                  "primary": "Ask two questions to GPT: 1 factual, 1 creative.",
                  "secondary": "Compare outputs. Discuss: Was it smart or just trained?"
                }
              ]
            },
            {
              "part": 3,
              "name": "Practice & Application: What is Smart?",
              "duration": 15,
              "activities": [
                {
                  "icon": "WritingIcon",
                  "primary": "Students write their own definition of 'smart'.",
                  "secondary": "Then ask the AI the same question. Compare and discuss."
                },
                {
                  "icon": "SortIcon",
                  "primary": "Group sorting activity: Rule Follower vs Learner.",
                  "secondary": "Students justify their sort choices with peers."
                }
              ]
            },
            {
              "part": 4,
              "name": "Plenary: Reflection & Wonder",
              "duration": 7,
              "activities": [
                {
                  "icon": "DiscussionIcon",
                  "primary": "Ask students to share one thing they learned and one question they still have.",
                  "secondary": "Use prompts like 'I wonder…' and 'AI surprised me when…'"
                }
              ]
            }
          ]
        }
      },
      {
        "name": "Assessment & Homework",
        "componentType": "assessment",
        "content": [
          {
            "sectionName": "Assessment Criteria",
            "title": "Evaluating Student Thinking",
            "description": "Students will be assessed on their ability to:\n✔️ Distinguish between AI and rule-based technologies.\n✔️ Articulate how AI uses data and predictions.\n✔️ Reflect thoughtfully on how AI 'thinks' vs humans.\n✔️ Engage with key ethical or conceptual questions about AI use.",
            "icon": "CheckIcon"
          },
          {
            "sectionName": "Homework Task",
            "title": "Family AI Interview",
            "description": "Ask someone at home: 'Do we use any smart machines like Alexa, YouTube suggestions, or a robot vacuum?'\nDraw or explain how it helps, and whether it learns or just follows instructions.",
            "icon": "BookIcon"
          }
        ]
      }
    ]
  },


  {
    "metadata": {
      "id": "computing_y1_week1a_slides",
      "classTitle": "Foundations – AI Around Us -3mini",
      "keyStage": 1,
      "week": 1,
      "order": 1,
      "module": "What is AI?",
      "subheading": "Foundations – AI Around Us",
      "lesson": "Week 1A",
      "yearGroup": "Year 1",
      "title": "Week 1: What is AI? - 3 mini",
      "description": "Students will explore the concept of AI, differentiate it from traditional machines, and identify its real-life applications through interactive activities and creative reflection.",
      "curriculumAlignment": "UAE Cycle 1 / KS1 Computing – Recognise common uses of information technology beyond school. Understand that programs execute by following precise instructions.",
      "keyFocus": "Conceptual clarity through inquiry, guided exploration, and creative reflection.",
      "aiIntegration": "Students will engage with AI concepts through activities, discussions, and the use of an AI assistant."
    },
    "slides": [
      {
        "title": "🌟 Welcome to AI Adventures!",
        "content": "Today, we're going to explore the amazing world of Artificial Intelligence (AI) and discover how it helps us every day!",
        "image": "welcome_ai_adventures.jpg",
        "speakerNotes": {
          "objective": "Introduce students to the topic of AI and set the tone for an engaging lesson.",
          "questions": [
            "What do you think AI is?",
            "Have you seen any AI around you?"
          ],
          "imagePrompt": "A colorful image of children engaging with technology.",
          "scaffoldingTips": {
            "support": "Encourage students to share their experiences with technology.",
            "core": "Highlight the importance of curiosity in learning.",
            "challenge": "Ask students to think of the most advanced technology they know."
          },
          "teacherTip": "Use a friendly tone and encourage participation from all students.",
          "aiPrompt": "N/A"
        }
      },
      {
        "title": "🔍 AI All Around Us!",
        "content": "Can you spot AI in your daily life? Let’s look at some images and see what we can find!",
        "image": "ai_in_daily_life.jpg",
        "speakerNotes": {
          "objective": "Help students recognize common AI technologies in their lives.",
          "questions": [
            "What do these machines have in common?",
            "Which one do you use the most?"
          ],
          "imagePrompt": "Images of AI devices like smart speakers, robots, and smart home devices.",
          "scaffoldingTips": {
            "support": "Provide examples of AI they might not recognize.",
            "core": "Encourage students to share personal stories related to AI.",
            "challenge": "Ask students to think of new AI inventions they would like to see."
          },
          "teacherTip": "Use AI Bingo to make the activity interactive and fun.",
          "aiPrompt": "N/A"
        }
      },
      {
        "title": "📚 Story Time: Mila Asks!",
        "content": "Join Mila as she wonders if her pencil case is smart! Does it know what she needs?",
        "image": "mila_story_time.jpg",
        "speakerNotes": {
          "objective": "Introduce the concept of programmed rules vs. learning machines through a story.",
          "questions": [
            "What do you think makes something smart?",
            "Can a pencil case really learn?"
          ],
          "imagePrompt": "An illustration of Mila with her colorful pencil case.",
          "scaffoldingTips": {
            "support": "Read slowly and emphasize key points in the story.",
            "core": "Facilitate a discussion on the story's themes.",
            "challenge": "Ask students to create their own short stories about smart objects."
          },
          "teacherTip": "Use expressive reading to engage students during the story.",
          "aiPrompt": "N/A"
        }
      },
      {
        "title": "🕵️‍♂️ Real or Not? AI Mystery Game!",
        "content": "Let’s play a game! Is this AI or just a tool? Let’s find out!",
        "image": "mystery_machine_game.jpg",
        "speakerNotes": {
          "objective": "Help students differentiate between AI and traditional tools through a fun game.",
          "questions": [
            "What makes something AI?",
            "What does it need to do?"
          ],
          "imagePrompt": "Flashcards showing different machines like a smart fridge and a microwave.",
          "scaffoldingTips": {
            "support": "Provide hints to help students think critically about each machine.",
            "core": "Encourage group discussions after each round.",
            "challenge": "Ask students to explain their reasoning for each choice."
          },
          "teacherTip": "Keep the atmosphere light and fun to encourage participation.",
          "aiPrompt": "Ask AIDEN GPT: 'Can you tell me if this is AI or not?'"
        }
      },
      {
        "title": "💡 Mila's Challenge: Design an AI Helper!",
        "content": "What AI helper can we create for Mila? Let’s design something that helps her in class!",
        "image": "design_ai_helper.jpg",
        "speakerNotes": {
          "objective": "Encourage creativity and critical thinking by having students design their own AI helpers.",
          "questions": [
            "What would Mila's AI need to see or hear?",
            "How would it help her in class?"
          ],
          "imagePrompt": "Students drawing their AI helpers with various features.",
          "scaffoldingTips": {
            "support": "Provide examples of features that AI can have.",
            "core": "Facilitate brainstorming sessions in small groups.",
            "challenge": "Ask students to think about how their AI could learn new things."
          },
          "teacherTip": "Encourage sharing of ideas and collaboration among students.",
          "aiPrompt": "N/A"
        }
      },
      {
        "title": "🤝 Share + Reflect!",
        "content": "Let’s share our AI ideas with a partner! What does your AI do for Mila?",
        "image": "share_reflect.jpg",
        "speakerNotes": {
          "objective": "Promote peer learning and reflection on their designs.",
          "questions": [
            "What was your favorite AI idea?",
            "How does your AI help Mila?"
          ],
          "imagePrompt": "Students discussing and sharing their drawings.",
          "scaffoldingTips": {
            "support": "Encourage students to give positive feedback to each other.",
            "core": "Guide them to think about the features of their AI.",
            "challenge": "Ask them to consider how their AI could improve over time."
          },
          "teacherTip": "Monitor discussions to ensure all students are engaged.",
          "aiPrompt": "N/A"
        }
      },
      {
        "title": "📝 Metacognitive Reflection",
        "content": "Let’s think about what we learned today! How do we know something is AI?",
        "image": "metacognitive_reflection.jpg",
        "speakerNotes": {
          "objective": "Encourage students to reflect on their learning and understanding of AI.",
          "questions": [
            "Can machines think like us?",
            "Did I build something that reacts or learns?"
          ],
          "imagePrompt": "A thought bubble with questions about AI.",
          "scaffoldingTips": {
            "support": "Provide sentence starters for students to express their thoughts.",
            "core": "Encourage them to think critically about AI.",
            "challenge": "Ask them to consider future implications of AI."
          },
          "teacherTip": "Use a reflective journal to collect students' thoughts.",
          "aiPrompt": "N/A"
        }
      },
      {
        "title": "🏠 Exit Task: Homework Challenge!",
        "content": "For homework, draw your AI helper and write: 'My AI helps Mila by _____.'",
        "image": "homework_challenge.jpg",
        "speakerNotes": {
          "objective": "Reinforce learning by having students create a tangible representation of their ideas.",
          "questions": [
            "What will you draw for Mila?",
            "How can your AI help her?"
          ],
          "imagePrompt": "A blank drawing template with a prompt for students.",
          "scaffoldingTips": {
            "support": "Provide examples of how to complete the drawing and sentence.",
            "core": "Encourage creativity in their drawings.",
            "challenge": "Ask them to think of a new feature for their AI."
          },
          "teacherTip": "Collect homework to assess understanding of AI concepts.",
          "aiPrompt": "N/A"
        }
      }
    ],
    "teacherMaterials": [
      {
        "name": "Lesson Plan & Objectives",
        "componentType": "lessonplanObjectives",
        "content": [
          {
            "sectionName": "Learning Objectives",
            "title": "What Students Will Learn",
            "description": "By the end of this lesson, students will:\n✅ Define “AI” in their own words using simple analogies\n✅ Recognize that AI means machines can “learn,” “see,” “hear,” or “talk”\n✅ Differentiate between traditional machines and AI-enabled ones\n✅ Identify real-life uses of AI in their home/school life\n✅ Reflect metacognitively on how AI mimics (or differs from) human thinking",
            "icon": "TargetIcon"
          }
        ],
        "lessonPlan": {
          "sections": [
            {
              "part": 1,
              "name": "AI All Around Us!",
              "duration": 10,
              "activities": [
                {
                  "icon": "ImageIcon",
                  "primary": "Show image cards of AI in daily life.",
                  "secondary": "Prompt students with: 'What do these machines have in common?' Engage them in AI Bingo to check off AI they've seen."
                }
              ]
            },
            {
              "part": 2,
              "name": "Story Time – Mila Asks: Is My Pencil Case Smart?",
              "duration": 10,
              "activities": [
                {
                  "icon": "BookIcon",
                  "primary": "Read the illustrated story about Mila and her pencil case.",
                  "secondary": "Discuss whether the pencil case knows or if it was packed by someone. Differentiate programmed rules from learning machines."
                }
              ]
            },
            {
              "part": 3,
              "name": "Real or Not? – AI or Just a Tool",
              "duration": 10,
              "activities": [
                {
                  "icon": "GameIcon",
                  "primary": "Play the Mystery Machine Game with flashcards.",
                  "secondary": "Ask students to determine if the items are AI or not, and discuss what makes something AI. Use AIDEN GPT for a fun interaction."
                }
              ]
            },
            {
              "part": 4,
              "name": "Mila's Challenge – Build Her a Real AI Helper!",
              "duration": 10,
              "activities": [
                {
                  "icon": "CreativeIcon",
                  "primary": "Students design an AI helper for Mila.",
                  "secondary": "Prompt them with: 'What would Mila’s AI need to see, hear, or know to help her in class?'"
                }
              ]
            },
            {
              "part": 5,
              "name": "Share + Reflect",
              "duration": 5,
              "activities": [
                {
                  "icon": "DiscussionIcon",
                  "primary": "Students share their AI ideas in pairs.",
                  "secondary": "Use the class journal to draw their AI and write: 'My AI helps Mila by _____.'"
                }
              ]
            }
          ]
        }
      },
      {
        "name": "Assessment & Homework",
        "componentType": "assessment",
        "content": [
          {
            "sectionName": "Assessment Criteria",
            "title": "Assessment of Learning",
            "description": "Students should be able to:\n✅ Describe AI as a machine that can “learn,” “see,” or “talk”\n✅ Identify examples of AI they’ve seen in real life\n✅ Differentiate AI from rule-based machines\n✅ Design a simple AI helper idea for classroom use\n✅ Reflect on the meaning of intelligence and learning in machines",
            "icon": "CheckIcon"
          },
          {
            "sectionName": "Homework Task",
            "title": "AI in My Life",
            "description": "Students will write or draw at least two examples of AI they encounter at home or school and explain how they work.",
            "icon": "BookIcon"
          }
        ]
      },
      {
        "name": "Metacognitive Reflection",
        "componentType": "metacognitiveExercise",
        "content": [
          {
            "sectionName": "Reflection Prompts",
            "title": "Thinking About AI",
            "description": "Encourage students to think deeply with these prompts:\n- 'How do I know something is AI?'\n- 'Can machines think like us?'\n- 'Did I build something that reacts or learns?'",
            "icon": "ReflectionIcon"
          }
        ]
      },
      {
        "name": "Materials Needed",
        "componentType": "materials",
        "content": [
          {
            "sectionName": "Teacher Prep List",
            "title": "Materials for Today's Class",
            "description": "🖼️ AI Bingo sheets\n🎴 Mystery Machine Game cards\n📝 Drawing templates and markers\n🎤 AIDEN GPT access or voice assistant demo",
            "icon": "ClipboardIcon"
          }
        ]
      }
    ]
  },
  {
    "metadata": {
      "id": "computing_y1_week1a_slides",
      "classTitle": "Foundations – Computer Vision Without Faces - 3 mini",
      "keyStage": 1,
      "week": 1,
      "order": 1,
      "module": "Computer Vision",
      "subheading": "Foundations – Computer Vision Without Faces",
      "lesson": "Week 1A",
      "yearGroup": "Year 1",
      "title": "Week 1: How AI Sees the World - 3 mini",
      "description": "Students learn how AI recognizes patterns using shapes, colors, and visual data through engaging activities and discussions.",
      "curriculumAlignment": "UAE Cycle 1 / KS1 Computing – Understand that AI systems recognise patterns using cameras and software",
      "keyFocus": "Visual perception, data sorting, and machine 'vision' through safe analogies",
      "aiIntegration": "Understanding AI's pattern recognition through visual data"
    },
    "slides": [
      {
        "title": "👋 Welcome to AI Vision!",
        "content": "Today, we’ll explore how AI can 'see' and sort objects just like us!",
        "image": "welcome_ai_vision.jpg",
        "speakerNotes": {
          "objective": "Introduce students to the concept of AI and computer vision.",
          "questions": [
            "What do you think AI can see?",
            "How do we see things?"
          ],
          "imagePrompt": "A colorful classroom with children looking excited about learning.",
          "scaffoldingTips": {
            "support": "Use simple language and examples they understand.",
            "core": "Encourage students to share what they know about robots.",
            "challenge": "Ask advanced students to think of more complex AI examples."
          },
          "teacherTip": "Start with a relatable example to spark interest.",
          "aiPrompt": "What are some common tasks AI can help with?"
        }
      },
      {
        "title": "📖 Mila’s Robot Sorter Story",
        "content": "Mila built a robot to help her sort art supplies, but it keeps mixing them up! Why?",
        "image": "mila_robot_sorter.jpg",
        "speakerNotes": {
          "objective": "Engage students with a story to introduce the problem.",
          "questions": [
            "What does the robot need to see?",
            "Why is it confused?"
          ],
          "imagePrompt": "An illustration of Mila and her confused robot.",
          "scaffoldingTips": {
            "support": "Read the story together to ensure understanding.",
            "core": "Discuss the robot's challenges and relate them to AI.",
            "challenge": "Ask students how they would fix the robot's problem."
          },
          "teacherTip": "Use expressive reading to make the story engaging.",
          "aiPrompt": "How might AI sort things differently than humans?"
        }
      },
      {
        "title": "👁️ How AI Sees Shapes & Colors",
        "content": "AI uses cameras to see patterns, shapes, and colors. It doesn’t have eyes like us!",
        "image": "ai_sees_shapes_colors.jpg",
        "speakerNotes": {
          "objective": "Explain how AI perceives visual data.",
          "questions": [
            "What patterns can you see in this picture?",
            "How do colors help us identify things?"
          ],
          "imagePrompt": "A diagram showing a camera and patterns it detects.",
          "scaffoldingTips": {
            "support": "Use visuals to demonstrate how AI identifies shapes.",
            "core": "Encourage students to point out shapes and colors in the classroom.",
            "challenge": "Ask advanced students to think of how AI might see differently."
          },
          "teacherTip": "Use real-world examples of AI in cameras (like phones).",
          "aiPrompt": "What patterns do you notice in everyday objects?"
        }
      },
      {
        "title": "🔍 Object Sorting Challenge!",
        "content": "Let’s sort objects like AI! You will use one rule at each station.",
        "image": "sorting_challenge.jpg",
        "speakerNotes": {
          "objective": "Engage students in hands-on sorting activities.",
          "questions": [
            "What rule will you use to sort these objects?",
            "Can AI make mistakes?"
          ],
          "imagePrompt": "Children at sorting stations with various objects.",
          "scaffoldingTips": {
            "support": "Provide clear examples of sorting rules.",
            "core": "Facilitate the activity and encourage teamwork.",
            "challenge": "Ask students to create more complex sorting rules."
          },
          "teacherTip": "Rotate groups to keep energy high and engagement strong.",
          "aiPrompt": "How would you explain your sorting rule to an AI?"
        }
      },
      {
        "title": "🤖 Design Your AI Sorting Robot!",
        "content": "Now, let’s get creative! Design your own AI robot that can sort objects.",
        "image": "design_your_robot.jpg",
        "speakerNotes": {
          "objective": "Encourage creativity and application of learning.",
          "questions": [
            "What features will your robot have?",
            "How will it know what to sort?"
          ],
          "imagePrompt": "Children drawing and using cutouts to design robots.",
          "scaffoldingTips": {
            "support": "Provide templates or examples for students to follow.",
            "core": "Encourage sharing of ideas among peers.",
            "challenge": "Ask advanced students to explain their robot's sorting logic."
          },
          "teacherTip": "Display student designs to celebrate creativity.",
          "aiPrompt": "What special abilities would your robot have?"
        }
      },
      {
        "title": "📝 Reflect & Journal Time!",
        "content": "Write about your AI robot! 'My AI robot sees ___, and it knows to do ___.'",
        "image": "journal_reflection.jpg",
        "speakerNotes": {
          "objective": "Encourage reflection on the lesson.",
          "questions": [
            "What was the most fun part of today?",
            "What did you learn about AI?"
          ],
          "imagePrompt": "A child writing in a journal with a thoughtful expression.",
          "scaffoldingTips": {
            "support": "Provide sentence starters for students who need help.",
            "core": "Encourage sharing in pairs before writing.",
            "challenge": "Ask advanced students to write a short story about their robot."
          },
          "teacherTip": "Collect journals to assess understanding later.",
          "aiPrompt": "What would you want your robot to learn next?"
        }
      },
      {
        "title": "🚀 Exit Task: AI Challenge!",
        "content": "For homework, think of something AI could sort in your home. Draw it!",
        "image": "ai_exit_task.jpg",
        "speakerNotes": {
          "objective": "Extend learning beyond the classroom.",
          "questions": [
            "What items at home could AI help sort?",
            "How would it do that?"
          ],
          "imagePrompt": "A home environment with various objects to sort.",
          "scaffoldingTips": {
            "support": "Provide examples of common household items.",
            "core": "Encourage creativity in their drawings.",
            "challenge": "Ask students to explain their sorting method in detail."
          },
          "teacherTip": "Remind students to be creative and have fun with their drawings.",
          "aiPrompt": "How could you explain your drawing to an AI?"
        }
      }
    ],
    "teacherMaterials": [
      {
        "name": "Lesson Plan & Objectives",
        "componentType": "lessonplanObjectives",
        "content": [
          {
            "sectionName": "Learning Objectives",
            "title": "What Students Will Learn",
            "description": "By the end of this lesson, students will:\n✅ Describe how AI can “see” using patterns, shapes, and colors\n✅ Understand that computer vision is about recognizing visual data\n✅ Explain why AI sometimes gets confused\n✅ Practice sorting like an AI using rule-based visual clues",
            "icon": "TargetIcon"
          }
        ],
        "lessonPlan": {
          "sections": [
            {
              "part": 1,
              "name": "Mila’s Robot Sorter",
              "duration": 7,
              "activities": [
                {
                  "icon": "StoryIcon",
                  "primary": "Introduce the story of Mila and her robot that mixes up art supplies.",
                  "secondary": "Discuss why the robot cannot tell the difference between paintbrushes and markers."
                }
              ]
            },
            {
              "part": 2,
              "name": "How AI “Sees” Shapes & Colors",
              "duration": 10,
              "activities": [
                {
                  "icon": "LessonIcon",
                  "primary": "Explain how AI uses cameras to recognize patterns.",
                  "secondary": "Use visuals to demonstrate how shapes and colors help AI."
                }
              ]
            },
            {
              "part": 3,
              "name": "Object Sorting Challenge",
              "duration": 15,
              "activities": [
                {
                  "icon": "ActivityIcon",
                  "primary": "Set up stations where students sort objects based on specific rules.",
                  "secondary": "Encourage students to think about what mistakes the AI might make."
                }
              ]
            },
            {
              "part": 4,
              "name": "Design an AI Sorting Robot!",
              "duration": 10,
              "activities": [
                {
                  "icon": "CreativeIcon",
                  "primary": "Students sketch or create a model of their own AI sorting robot.",
                  "secondary": "Encourage creativity in how the robot would sort objects."
                }
              ]
            },
            {
              "part": 5,
              "name": "Reflect + Journal",
              "duration": 5,
              "activities": [
                {
                  "icon": "JournalIcon",
                  "primary": "Students write in their journals about what their AI robot sees and does.",
                  "secondary": "Optional sharing circle to discuss challenges in AI vision."
                }
              ]
            }
          ]
        }
      },
      {
        "name": "Assessment & Homework",
        "componentType": "assessment",
        "content": [
          {
            "sectionName": "Assessment Criteria",
            "title": "How We Will Assess Learning",
            "description": "Students will be assessed on the following:\n✅ Explain that AI sees with cameras and finds patterns\n✅ Participate in sorting with a clear visual rule\n✅ Design an AI robot using shape, color, or size logic\n✅ Reflect on what makes computer vision work (or not)",
            "icon": "CheckIcon"
          },
          {
            "sectionName": "Homework Task",
            "title": "AI Robot Reflection",
            "description": "At home, students can draw another type of robot that can help with a different task and explain how it would see and sort.",
            "icon": "BookIcon"
          }
        ]
      },
      {
        "name": "Metacognitive Reflection",
        "componentType": "metacognitiveExercise",
        "content": [
          {
            "sectionName": "Reflection Prompts",
            "title": "Thinking About Our Learning",
            "description": "Students will consider:\n- What rule did I follow to sort the objects?\n- How do machines know what they’re looking at?\n- What could confuse a robot eye?",
            "icon": "ReflectionIcon"
          }
        ]
      },
      {
        "name": "Materials Needed",
        "componentType": "materials",
        "content": [
          {
            "sectionName": "Teacher Prep List",
            "title": "Materials for Today's Class",
            "description": "Gather the following materials:\n📖 Mila’s “Robot Sorter” story comic\n🔵 Sorting objects: colored blocks, shape cards, size cards\n📝 Drawing paper or AI Robot template\n📓 AI Journals\n🏷️ Station signs: Color / Shape / Size",
            "icon": "ClipboardIcon"
          }
        ]
      }
    ]
  },
  {
    "metadata": {
      "id": "computing_y1_week1a_slides",
      "classTitle": "Pattern Recognition-3mini",
      "keyStage": 1,
      "week": 1,
      "order": 1,
      "module": "Understanding Data and Classification",
      "subheading": "Pattern Recognition – What AI Notices and Sorts",
      "lesson": "Week 1A",
      "yearGroup": "Year 1",
      "title": "Week 1: Patterns Everywhere! - 3 mini",
      "description": "Students will explore pattern recognition through sorting activities and understand how AI uses patterns to make decisions.",
      "curriculumAlignment": "UAE Cycle 1 / KS1 Computing – Understand the use of data and classification.",
      "keyFocus": "Data thinking + multisensory sorting + classification logic",
      "aiIntegration": "Students learn how AI uses patterns to recognize and sort objects."
    },
    "slides": [
      {
        "title": "👋 Welcome to Pattern Recognition!",
        "content": "Today, we will explore how AI notices patterns and sorts things. Get ready to discover the world of patterns!",
        "image": "welcome_pattern.jpg",
        "speakerNotes": {
          "objective": "Introduce the topic and engage students.",
          "questions": [
            "What do you think a pattern is?",
            "Can you find a pattern in the classroom?"
          ],
          "imagePrompt": "A colorful classroom setting with various objects showing patterns.",
          "scaffoldingTips": {
            "support": "Use simple language and examples.",
            "core": "Encourage students to share their thoughts.",
            "challenge": "Ask students to think of patterns outside the classroom."
          },
          "teacherTip": "Use a fun, upbeat tone to keep students excited.",
          "aiPrompt": "What are some examples of patterns in nature?"
        }
      },
      {
        "title": "🔍 What is a Pattern?",
        "content": "Patterns can be made of color, shape, or size. Can you spot the patterns around you?",
        "image": "pattern_examples.jpg",
        "speakerNotes": {
          "objective": "Define patterns and their features.",
          "questions": [
            "What colors do you see?",
            "Can you name different shapes?"
          ],
          "imagePrompt": "Images of various patterns (stripes, dots, shapes).",
          "scaffoldingTips": {
            "support": "Provide physical examples for students to touch.",
            "core": "Discuss different types of patterns together.",
            "challenge": "Ask students to create their own patterns."
          },
          "teacherTip": "Use real-life examples to make it relatable.",
          "aiPrompt": "Can you list different types of patterns in everyday life?"
        }
      },
      {
        "title": "🤖 Mila's Sorting Success!",
        "content": "Mila teaches her robot to sort blocks by color. How does the robot know where to put each block?",
        "image": "mila_robot_sorting.jpg",
        "speakerNotes": {
          "objective": "Introduce the concept of AI sorting.",
          "questions": [
            "What rules do you think the robot follows?",
            "How can we teach a robot to sort?"
          ],
          "imagePrompt": "A girl with a robot sorting colorful blocks.",
          "scaffoldingTips": {
            "support": "Break down the sorting process into simple steps.",
            "core": "Discuss how rules help AI sort objects.",
            "challenge": "Ask students how they would teach a robot different rules."
          },
          "teacherTip": "Use storytelling to make the concept engaging.",
          "aiPrompt": "What rules could a robot use to sort different objects?"
        }
      },
      {
        "title": "🛠️ Sorting Stations Activity!",
        "content": "Let's rotate through 3 sorting stations! Remember to share the rule you are using!",
        "image": "sorting_stations.jpg",
        "speakerNotes": {
          "objective": "Engage students in hands-on sorting activities.",
          "questions": [
            "What rule are you using at this station?",
            "How can you explain your sorting?"
          ],
          "imagePrompt": "Students actively sorting objects at different stations.",
          "scaffoldingTips": {
            "support": "Provide examples of rules for students to follow.",
            "core": "Encourage collaboration and sharing at each station.",
            "challenge": "Ask students to come up with their own unique sorting rules."
          },
          "teacherTip": "Monitor stations and facilitate discussions.",
          "aiPrompt": "How might a robot sort these objects differently?"
        }
      },
      {
        "title": "📝 Reflect + Journal Time!",
        "content": "How does AI find patterns? What makes sorting tricky? Draw something you sorted and write the rule you used.",
        "image": "journal_time.jpg",
        "speakerNotes": {
          "objective": "Encourage reflection on the day's learning.",
          "questions": [
            "What was the easiest part of sorting?",
            "What was the hardest part?"
          ],
          "imagePrompt": "A student drawing in a journal.",
          "scaffoldingTips": {
            "support": "Provide sentence starters for their reflections.",
            "core": "Encourage students to express their thoughts creatively.",
            "challenge": "Ask students to think of new sorting methods."
          },
          "teacherTip": "Allow time for sharing reflections with the class.",
          "aiPrompt": "What new insights did you gain about patterns today?"
        }
      },
      {
        "title": "🌟 Metacognitive Moments",
        "content": "Let's think about our thinking! How do we decide what goes together?",
        "image": "metacognition.jpg",
        "speakerNotes": {
          "objective": "Promote metacognitive reflection.",
          "questions": [
            "How might a machine get confused?",
            "Did I change my thinking to find a new rule?"
          ],
          "imagePrompt": "A thought bubble with questions about thinking.",
          "scaffoldingTips": {
            "support": "Encourage students to share their thought processes.",
            "core": "Discuss the importance of understanding our own thinking.",
            "challenge": "Ask students to think of examples from their own lives."
          },
          "teacherTip": "Use a circle time format to facilitate discussion.",
          "aiPrompt": "What strategies help you understand patterns better?"
        }
      },
      {
        "title": "✅ Exit Task/Homework",
        "content": "For homework, find 3 patterns at home and write down the rules you used to identify them!",
        "image": "homework_patterns.jpg",
        "speakerNotes": {
          "objective": "Encourage students to apply learning at home.",
          "questions": [
            "What patterns can you find at home?",
            "How will you describe the rules?"
          ],
          "imagePrompt": "A child looking for patterns in their home.",
          "scaffoldingTips": {
            "support": "Provide examples of patterns to look for.",
            "core": "Encourage creativity in finding patterns.",
            "challenge": "Ask students to present their findings in class."
          },
          "teacherTip": "Remind students to bring their findings to share.",
          "aiPrompt": "What patterns did you find that surprised you?"
        }
      }
    ],
    "teacherMaterials": [
      {
        "name": "Lesson Plan & Objectives",
        "componentType": "lessonplanObjectives",
        "content": [
          {
            "sectionName": "Learning Objectives",
            "title": "What Students Will Learn",
            "description": "By the end of this lesson, students will:\n✅ Recognise what a pattern is (color, shape, size)\n✅ Explain how AI uses patterns to recognise and sort\n✅ Sort physical objects and describe patterns using key features\n✅ Reflect on why pattern recognition helps machines learn",
            "icon": "TargetIcon"
          }
        ],
        "lessonPlan": {
          "sections": [
            {
              "part": 1,
              "name": "What’s the Pattern?",
              "duration": 10,
              "activities": [
                {
                  "icon": "PuzzleIcon",
                  "primary": "Show a row of objects and ask: 'What comes next?' (color sequence, shape order)",
                  "secondary": "Prompt the students to think about the rules their brains are using to identify the pattern."
                }
              ]
            },
            {
              "part": 2,
              "name": "Mila's Sorting Success!",
              "duration": 5,
              "activities": [
                {
                  "icon": "StoryIcon",
                  "primary": "Tell a story about Mila and her toy robot sorting blocks by color.",
                  "secondary": "Discuss how the robot knows where to put each block based on clear visual rules."
                }
              ]
            },
            {
              "part": 3,
              "name": "Sorting Stations",
              "duration": 20,
              "activities": [
                {
                  "icon": "ActivityIcon",
                  "primary": "Students rotate through 3 sorting stations with different objects.",
                  "secondary": "At each station, students share the rule they are using to sort with a partner."
                }
              ]
            },
            {
              "part": 4,
              "name": "Reflect + Journal",
              "duration": 5,
              "activities": [
                {
                  "icon": "JournalIcon",
                  "primary": "Prompt students to reflect on how AI finds patterns and what makes sorting tricky.",
                  "secondary": "Students draw one thing they sorted and write the rule they used."
                }
              ]
            }
          ]
        }
      },
      {
        "name": "Assessment & Homework",
        "componentType": "assessment",
        "content": [
          {
            "sectionName": "Assessment Criteria",
            "title": "Assessment of Understanding",
            "description": "Students should be able to:\n✅ Define a pattern and name key features (color, shape, size)\n✅ Sort using a clear rule and explain their reasoning\n✅ Roleplay as an AI and follow classification rules\n✅ Reflect on how AI uses data to recognise patterns",
            "icon": "CheckIcon"
          },
          {
            "sectionName": "Homework Task",
            "title": "Pattern Hunt at Home",
            "description": "Find 3 examples of patterns in your home (e.g., in clothing, tiles, or nature) and write down the rules you used to identify them.",
            "icon": "BookIcon"
          }
        ]
      },
      {
        "name": "Metacognitive Reflection",
        "componentType": "metacognitiveExercise",
        "content": [
          {
            "sectionName": "Reflection Prompts",
            "title": "Thinking About Our Thinking",
            "description": "Use these prompts to guide your reflection:\n- How do I decide what goes together?\n- How might a machine get confused?\n- Did I change my thinking to find a new rule?",
            "icon": "ReflectionIcon"
          }
        ]
      },
      {
        "name": "Materials Needed",
        "componentType": "materials",
        "content": [
          {
            "sectionName": "Teacher Prep List",
            "title": "Materials for Today's Class",
            "description": "Gather the following materials:\n- 🟦 Color blocks\n- 🔺 Shape cards\n- 🗃️ Sorting trays\n- 📋 Station signs and rule sheets\n- ✏️ Drawing materials",
            "icon": "ClipboardIcon"
          }
        ]
      }
    ]
  },


  {
    "metadata": {
      "id": "AI_y1_week1A_slides",
      "classTitle": "What is AI?",
      "keyStage": 1,
      "week": 1,
      "order": 1,
      "module": "Foundations – AI Around Us",
      "subheading": "AI Around Us",
      "lesson": "Week 1A",
      "yearGroup": "Year 1",
      "title": "Week 1: What is AI?",
      "description": "This lesson introduces students to the concept of artificial intelligence (AI), its common uses, and how it differs from traditional machines. Students will engage in various activities to understand AI and reflect on its implications.",
      "curriculumAlignment": "UAE Cycle 1 / KS1 Computing – Recognise common uses of information technology beyond school. Understand that programs execute by following precise instructions.",
      "keyFocus": "Conceptual clarity through inquiry, guided exploration, and creative reflection",
      "aiIntegration": "AI is integrated into the lesson as the main topic of study. Students will engage with AI through various activities such as AI Bingo, Mystery Machine Game, and designing an AI helper. They will also interact with an AI model, AIDEN GPT."
    },
    "slides": [
      {
        "title": "🤖 Welcome to AI World!",
        "content": "Today we're going to explore what AI is and how it's used in our daily lives.",
        "image": "welcome_to_ai_world.jpg",
        "speakerNotes": {
          "objective": "To introduce the topic of the lesson and capture students' attention.",
          "questions": [
            "Can anyone tell me what AI stands for?",
            "Have you heard about AI before?"
          ],
          "imagePrompt": "An exciting and colorful image of AI-related objects, like robots and computers.",
          "scaffoldingTips": {
            "support": "For struggling students, provide examples of AI in common devices like smartphones or video games.",
            "core": "Introduce the topic with enthusiasm to engage all students.",
            "challenge": "For advanced students, ask them to think of a device that might use AI."
          },
          "teacherTip": "Start the lesson with a fun fact about AI to spark students' curiosity.",
          "aiPrompt": ""
        }
      },
      {
        "title": "🔎 Exploring AI Around Us",
        "content": "Let's look at some examples of AI in our daily lives.",
        "image": "ai_examples.jpg",
        "speakerNotes": {
          "objective": "To familiarize students with AI and its common uses.",
          "questions": [
            "What do these machines have in common?",
            "Can you think of other examples of AI around us?"
          ],
          "imagePrompt": "Image of various AI-enabled devices like a smart speaker, self-driving car, and a robot vacuum cleaner.",
          "scaffoldingTips": {
            "support": "Prompt struggling students with examples like 'Alexa' or 'Siri'.",
            "core": "Discuss each example in the image and how AI is used in it.",
            "challenge": "Ask advanced students to explain how they think these devices might use AI."
          },
          "teacherTip": "Use real-life examples to make the concept of AI relatable.",
          "aiPrompt": ""
        }
      },
      {
        "title": "🕵️‍♀️ Is it AI or Just a Tool?",
        "content": "Let's play a game to find out if these machines are AI or just tools.",
        "image": "mystery_machine_game.jpg",
        "speakerNotes": {
          "objective": "To help students differentiate between AI and non-AI devices.",
          "questions": [
            "What makes something AI? What does it need to do?",
            "Can you tell if this machine is AI or just a tool?"
          ],
          "imagePrompt": "Image of different devices like a smart fridge, normal fan, chatbot, microwave, and Roomba.",
          "scaffoldingTips": {
            "support": "Explain to struggling students that AI devices can learn, see, or talk.",
            "core": "Discuss each device in the image and ask students to guess if it's AI or not.",
            "challenge": "Ask advanced students to explain why they think a device is AI or not."
          },
          "teacherTip": "Use this activity to clarify misconceptions about AI.",
          "aiPrompt": "Try asking AIDEN GPT a question aloud – 'Tell me a joke' or 'Can you help me plan lunch?'"
        }
      },
      {
        "title": "🎨 Let's Build an AI Helper!",
        "content": "Now it's time to design an AI helper for Mila.",
        "image": "mila_ai_helper.jpg",
        "speakerNotes": {
          "objective": "To encourage students to apply their understanding of AI.",
          "questions": [
            "What would Mila’s AI need to see, hear, or know to help her in class?",
            "Can you describe your AI helper?"
          ],
          "imagePrompt": "Image of a blank template for students to draw their AI helper.",
          "scaffoldingTips": {
            "support": "Help struggling students by suggesting ideas like an AI that can do math or read books.",
            "core": "Encourage students to be creative and think of how their AI helper could assist Mila.",
            "challenge": "Ask advanced students to think of how their AI helper could learn from Mila."
          },
          "teacherTip": "Encourage students to share their ideas and provide constructive feedback.",
          "aiPrompt": ""
        }
      },
      {
        "title": "📝 Reflect and Share",
        "content": "Let's reflect on what we've learned and share our AI helper designs.",
        "image": "reflect_and_share.jpg",
        "speakerNotes": {
          "objective": "To encourage metacognitive reflection and peer sharing.",
          "questions": [
            "How do you know something is AI?",
            "Can machines think like us?",
            "Did you build something that reacts or learns?"
          ],
          "imagePrompt": "Image of students sharing their work with each other.",
          "scaffoldingTips": {
            "support": "Guide struggling students through the reflection prompts.",
            "core": "Facilitate a class discussion on the reflection prompts.",
            "challenge": "Ask advanced students to explain how their AI helper learns."
          },
          "teacherTip": "Use this opportunity to reinforce key concepts about AI.",
          "aiPrompt": ""
        }
      },
      {
        "title": "🏁 Exit Task: AI in My Life",
        "content": "For homework, write a short paragraph about how AI is used in your daily life.",
        "image": "exit_task.jpg",
        "speakerNotes": {
          "objective": "To reinforce the lesson's concepts and encourage real-world connections.",
          "questions": [],
          "imagePrompt": "Image of a notebook with the homework prompt written on it.",
          "scaffoldingTips": {
            "support": "Provide examples of AI in daily life for struggling students.",
            "core": "Remind students to use examples of AI they've learned in class.",
            "challenge": "Ask advanced students to think of how AI could be used in the future."
          },
          "teacherTip": "Remind students to bring their homework to the next class for discussion.",
          "aiPrompt": ""
        }
      }
    ],
    "teacherMaterials": [
      {
        "name": "Lesson Plan & Objectives",
        "componentType": "lessonplanObjectives",
        "content": [
          {
            "sectionName": "Learning Objectives",
            "title": "What Students Will Learn",
            "description": "By the end of this lesson, students will:\n✅ Define “AI” in their own words using simple analogies\n✅ Recognize that AI means machines can “learn,” “see,” “hear,” or “talk”\n✅ Differentiate between traditional machines and AI-enabled ones\n✅ Identify real-life uses of AI in their home/school life\n✅ Reflect metacognitively on how AI mimics (or differs from) human thinking",
            "icon": "TargetIcon"
          }
        ],
        "lessonPlan": {
          "sections": [
            {
              "part": 1,
              "name": "AI All Around Us!",
              "duration": 10,
              "activities": [
                {
                  "icon": "ImageIcon",
                  "primary": "Show image cards of AI in daily life",
                  "secondary": "AI Bingo – students check off AI they've seen in daily life"
                }
              ]
            },
            {
              "part": 2,
              "name": "Story Time – Mila Asks: Is My Pencil Case Smart?",
              "duration": 10,
              "activities": [
                {
                  "icon": "BookIcon",
                  "primary": "Teacher reads a short illustrated story: Mila wonders if her pencil case is smart because it always has what she needs",
                  "secondary": "Discussion: Difference between programmed rules vs learning machines"
                }
              ]
            },
            {
              "part": 3,
              "name": "Real or Not? – AI or Just a Tool",
              "duration": 10,
              "activities": [
                {
                  "icon": "QuestionIcon",
                  "primary": "Mystery Machine Game – Is this AI or not? (flashcards with smart fridge, normal fan, chatbot, microwave, Roomba)",
                  "secondary": "Try asking AIDEN GPT a question aloud – “Tell me a joke” or “Can you help me plan lunch?”"
                }
              ]
            },
            {
              "part": 4,
              "name": "Mila's Challenge – Build Her a Real AI Helper!",
              "duration": 10,
              "activities": [
                {
                  "icon": "PencilIcon",
                  "primary": "Students design an AI helper for Mila – something to help with a school task using AI",
                  "secondary": "Prompt: “What would Mila’s AI need to see, hear, or know to help her in class?”"
                }
              ]
            },
            {
              "part": 5,
              "name": "Share + Reflect",
              "duration": 5,
              "activities": [
                {
                  "icon": "ShareIcon",
                  "primary": "Students share their AI ideas in pairs",
                  "secondary": "Class Journal Option: Students draw their AI for Mila + write: “My AI helps Mila by _____.”"
                }
              ]
            }
          ]
        }
      },
      {
        "name": "Assessment & Homework",
        "componentType": "assessment",
        "content": [
          {
            "sectionName": "Assessment Criteria",
            "title": "Assessment Details",
            "description": "Students should be able to:\n✅ Describe AI as a machine that can “learn,” “see,” or “talk”\n✅ Identify examples of AI they’ve seen in real life\n✅ Differentiate AI from rule-based machines\n✅ Design a simple AI helper idea for a classroom use\n✅ Reflect on the meaning of intelligence and learning in machines",
            "icon": "CheckIcon"
          },
          {
            "sectionName": "Homework Task",
            "title": "Homework Assignment",
            "description": "Students should reflect on the AI they designed for Mila and think of ways it could be improved or expanded. They should write a paragraph explaining their ideas.",
            "icon": "BookIcon"
          }
        ]
      },
      {
        "name": "Metacognitive Reflection",
        "componentType": "metacognitiveExercise",
        "content": [
          {
            "sectionName": "Reflection Prompts",
            "title": "Metacognitive Reflection",
            "description": "Prompt students with the following questions:\n• “How do I know something is AI?”\n• “Can machines think like us?”\n• “Did I build something that reacts or learns?”",
            "icon": "ReflectionIcon"
          }
        ]
      },
      {
        "name": "Materials Needed",
        "componentType": "materials",
        "content": [
          {
            "sectionName": "Teacher Prep List",
            "title": "Materials for Today's Class",
            "description": "For this class, you will need:\n• AI Bingo sheet\n• Mystery Machine Game cards\n• Drawing templates and markers\n• AIDEN GPT access (or teacher's voice assistant demo)",
            "icon": "ClipboardIcon"
          }
        ]
      }
    ]
  },
  {
    "metadata": {
      "id": "AI_y1_week1a_slides",
      "classTitle": "Foundations – Computer Vision Without Faces",
      "keyStage": 1,
      "week": 1,
      "order": 1,
      "module": "AI and Computer Vision",
      "subheading": "Foundations – Computer Vision Without Faces",
      "lesson": "Week 1A",
      "yearGroup": "Year 1",
      "title": "Week 1: How AI Sees the World",
      "description": "This lesson introduces students to the concept of computer vision and how AI systems recognize patterns using cameras and software. Students will engage in activities that demonstrate how AI 'sees' and sorts objects based on visual data.",
      "curriculumAlignment": "UAE Cycle 1 / KS1 Computing – Understand that AI systems recognise patterns using cameras and software",
      "keyFocus": "Visual perception, data sorting, and machine 'vision' through safe analogies",
      "aiIntegration": "The lesson integrates AI by explaining how AI systems use computer vision to recognize and sort visual data. Students will also design an AI robot and reflect on the challenges of computer vision."
    },
    "slides": [
      {
        "title": "🤖 Welcome to the World of AI!",
        "content": "Today we are learning about how AI can 'see' using patterns, shapes, and colors.",
        "image": "welcome_slide_image.jpg",
        "speakerNotes": {
          "objective": "To introduce the topic and set the stage for the lesson.",
          "questions": [
            "What do you think AI is?",
            "Can anyone tell me what AI stands for?"
          ],
          "imagePrompt": "An engaging image of a robot or AI.",
          "scaffoldingTips": {
            "support": "Provide a simple definition of AI for students who may struggle.",
            "core": "Introduce the topic of AI and gauge student's prior knowledge.",
            "challenge": "Ask advanced students if they can name any examples of AI in their daily life."
          },
          "teacherTip": "Keep the introduction light and engaging to capture student's interest.",
          "aiPrompt": ""
        }
      },
      {
        "title": "🔍 How AI 'Sees' Shapes & Colors",
        "content": "AI doesn't have eyes — it sees through a camera and finds patterns, like colors, shapes, and outlines.",
        "image": "ai_sees_shapes_colors.jpg",
        "speakerNotes": {
          "objective": "To explain the concept of how AI recognizes visual data.",
          "questions": [
            "How do you think AI can see shapes and colors?",
            "Why doesn't AI have eyes like us?"
          ],
          "imagePrompt": "A diagram showing how a camera captures an image and AI identifying patterns in it.",
          "scaffoldingTips": {
            "support": "Explain that AI uses cameras and software to recognize objects.",
            "core": "Discuss how AI identifies patterns, shapes, and colors.",
            "challenge": "Ask advanced students to think about how AI might 'see' in different ways than humans."
          },
          "teacherTip": "Use real-world examples of AI vision, such as self-driving cars or facial recognition.",
          "aiPrompt": ""
        }
      },
      {
        "title": "🧩 Object Sorting Challenge",
        "content": "Now it's time to act like an AI! We'll use one rule to sort objects at each station.",
        "image": "object_sorting_challenge.jpg",
        "speakerNotes": {
          "objective": "To practice sorting like an AI using rule-based visual clues.",
          "questions": [
            "What rule are you using to sort the objects?",
            "What could confuse an AI when sorting these objects?"
          ],
          "imagePrompt": "An image of the stations with the sorting objects.",
          "scaffoldingTips": {
            "support": "Give students a simple rule to follow for sorting, such as 'sort by color'.",
            "core": "Encourage students to think about how AI might sort objects.",
            "challenge": "Ask advanced students to come up with their own sorting rule."
          },
          "teacherTip": "Rotate the groups so that every student gets to try sorting with different rules.",
          "aiPrompt": ""
        }
      },
      {
        "title": "🎨 Design an AI Sorting Robot!",
        "content": "Now, let's design an imaginary AI robot that can sort objects using shape, color, or size logic.",
        "image": "design_ai_robot.jpg",
        "speakerNotes": {
          "objective": "To apply understanding of AI vision in a creative task.",
          "questions": [
            "What rule will your robot use to sort objects?",
            "How will your robot 'see' the objects?"
          ],
          "imagePrompt": "An image of a blank robot template.",
          "scaffoldingTips": {
            "support": "Provide examples and guidance for students who may struggle.",
            "core": "Encourage creativity while applying the principles learned.",
            "challenge": "Ask advanced students to consider how their robot could handle confusing objects."
          },
          "teacherTip": "Encourage students to share their designs and explain their thinking.",
          "aiPrompt": ""
        }
      },
      {
        "title": "📖 Reflect + Journal",
        "content": "Let's reflect on what we learned today. Journal prompt: 'My AI robot sees \\, and it knows to do \\.'",
        "image": "reflect_journal.jpg",
        "speakerNotes": {
          "objective": "To reflect on what makes computer vision work (or not).",
          "questions": [
            "What did you learn about AI vision today?",
            "What makes AI vision tricky?"
          ],
          "imagePrompt": "An image of an open journal with the prompt written.",
          "scaffoldingTips": {
            "support": "Provide sentence starters for students who may struggle with writing.",
            "core": "Encourage students to reflect on the lesson and their learning.",
            "challenge": "Ask advanced students to think about how AI could be improved."
          },
          "teacherTip": "Consider sharing some reflections aloud to foster a sense of community and shared learning.",
          "aiPrompt": ""
        }
      }
    ],
    "teacherMaterials": [
      {
        "name": "Lesson Plan & Objectives",
        "componentType": "lessonplanObjectives",
        "content": [
          {
            "sectionName": "Learning Objectives",
            "title": "What Students Will Learn",
            "description": "By the end of this lesson, students will:\n✅ Describe how AI can “see” using patterns, shapes, and colors\n✅ Understand that computer vision is about recognizing visual data\n✅ Explain why AI sometimes gets confused\n✅ Practice sorting like an AI using rule-based visual clues",
            "icon": "TargetIcon"
          }
        ],
        "lessonPlan": {
          "sections": [
            {
              "part": 1,
              "name": "Starter – Mila’s Robot Sorter",
              "duration": 7,
              "activities": [
                {
                  "icon": "StoryIcon",
                  "primary": "Story Hook: Mila built a helper robot to clean up her art supplies. But it keeps mixing up paintbrushes and markers!",
                  "secondary": "Class Discussion: What does the robot need to see to know what’s what?"
                }
              ]
            },
            {
              "part": 2,
              "name": "How AI “Sees” Shapes & Colors",
              "duration": 10,
              "activities": [
                {
                  "icon": "EyeIcon",
                  "primary": "Mini-Lesson: AI doesn't have eyes — it sees through a camera and finds patterns, like colors, shapes, and outlines",
                  "secondary": "Use visuals for better understanding"
                }
              ]
            },
            {
              "part": 3,
              "name": "Object Sorting Challenge",
              "duration": 15,
              "activities": [
                {
                  "icon": "SortIcon",
                  "primary": "At each station, students “act like the AI” using one rule",
                  "secondary": "Ask: If the AI made a mistake, what could it confuse?"
                }
              ]
            },
            {
              "part": 4,
              "name": "Design an AI Sorting Robot!",
              "duration": 10,
              "activities": [
                {
                  "icon": "DesignIcon",
                  "primary": "Students sketch or use cutouts to design an imaginary AI robot",
                  "secondary": "Encourage creativity and application of learned concepts"
                }
              ]
            },
            {
              "part": 5,
              "name": "Reflect + Journal",
              "duration": 5,
              "activities": [
                {
                  "icon": "ReflectionIcon",
                  "primary": "Journal prompt: “My AI robot sees ___, and it knows to do ___.”",
                  "secondary": "Optional sharing circle: What makes AI vision tricky?"
                }
              ]
            }
          ]
        }
      },
      {
        "name": "Assessment & Homework",
        "componentType": "assessment",
        "content": [
          {
            "sectionName": "Assessment Criteria",
            "title": "Lesson Assessment",
            "description": "✅ Explain that AI sees with cameras and finds patterns\n✅ Participate in sorting with a clear visual rule\n✅ Design an AI robot using shape, color, or size logic\n✅ Reflect on what makes computer vision work (or not)",
            "icon": "CheckIcon"
          }
        ]
      },
      {
        "name": "Metacognitive Reflection",
        "componentType": "metacognitiveExercise",
        "content": [
          {
            "sectionName": "Reflection Prompts",
            "title": "Metacognitive Prompts",
            "description": "• “What rule did I follow to sort the objects?”\n• “How do machines know what they’re looking at?”\n• “What could confuse a robot eye?”",
            "icon": "ReflectionIcon"
          }
        ]
      },
      {
        "name": "Materials Needed",
        "componentType": "materials",
        "content": [
          {
            "sectionName": "Teacher Prep List",
            "title": "Materials for Today's Class",
            "description": "• Mila’s “Robot Sorter” story comic\n• Sorting objects: colored blocks, shape cards, size cards\n• Drawing paper or AI Robot template\n• AI Journals\n• Station signs: Color / Shape / Size",
            "icon": "ClipboardIcon"
          }
        ]
      }
    ]
  },
  {
    "metadata": {
      "id": "AI_y1_week1a_slides",
      "classTitle": "Pattern Recognition – What AI Notices and Sorts",
      "keyStage": 1,
      "week": 1,
      "order": 1,
      "module": "Pattern Recognition",
      "subheading": "What AI Notices and Sorts",
      "lesson": "Week 1A",
      "yearGroup": "Year 1",
      "title": "Week 1: Pattern Recognition – What AI Notices and Sorts",
      "description": "This lesson introduces students to the concept of pattern recognition and how AI uses patterns to recognise and sort. Students will engage in activities to understand pattern recognition and reflect on why pattern recognition helps machines learn.",
      "curriculumAlignment": "UAE Cycle 1 / KS1 Computing – Understand the use of data and classification. Recognise how AI finds patterns to make decisions.",
      "keyFocus": "Data thinking + multisensory sorting + classification logic",
      "aiIntegration": "Students will learn how AI uses patterns to recognise and sort, and reflect on why pattern recognition helps machines learn."
    },
    "slides": [
      {
        "title": "👋 Welcome to Pattern Recognition",
        "content": "Today we're going to explore how AI uses patterns to recognise and sort things!",
        "image": "welcome_slide.jpg",
        "speakerNotes": {
          "objective": "Set the stage for the lesson and generate interest.",
          "questions": [
            "What do you know about patterns?",
            "Have you heard of AI before?"
          ],
          "imagePrompt": "Image of a friendly robot sorting blocks by color.",
          "scaffoldingTips": {
            "support": "Show examples of simple patterns.",
            "core": "Introduce the terms 'pattern' and 'AI'.",
            "challenge": "Ask students to think of a place they've seen a pattern in their own life."
          },
          "teacherTip": "Use energy and enthusiasm to make the introduction exciting.",
          "aiPrompt": "NA"
        }
      },
      {
        "title": "🔎 What's the Pattern?",
        "content": "Look at these objects. Can you guess what comes next in the sequence?",
        "image": "pattern_example.jpg",
        "speakerNotes": {
          "objective": "Introduce the concept of patterns and stimulate critical thinking.",
          "questions": [
            "What rules is your brain using to guess the next object?",
            "Why do you think that object comes next?"
          ],
          "imagePrompt": "Image of a sequence of colored shapes that form a pattern.",
          "scaffoldingTips": {
            "support": "Help students identify the pattern by pointing out repeating elements.",
            "core": "Discuss how our brains naturally recognize patterns.",
            "challenge": "Ask students to create their own pattern."
          },
          "teacherTip": "Encourage students to explain their reasoning.",
          "aiPrompt": "NA"
        }
      },
      {
        "title": "🤖 Mila's Sorting Success!",
        "content": "Mila's toy robot can sort blocks by color. How do you think it knows where to put each block?",
        "image": "mila_robot.jpg",
        "speakerNotes": {
          "objective": "Introduce the concept of AI sorting.",
          "questions": [
            "How did the robot know where to put each block?",
            "What rules might the robot be following?"
          ],
          "imagePrompt": "Image of Mila's toy robot sorting blocks into trays by color.",
          "scaffoldingTips": {
            "support": "Explain that the robot uses rules to sort the blocks.",
            "core": "Discuss how machines can follow clear visual rules.",
            "challenge": "Ask students how the robot might sort blocks by shape or size."
          },
          "teacherTip": "Use this story to make the concept of AI sorting relatable.",
          "aiPrompt": "NA"
        }
      },
      {
        "title": "🔄 Sorting Stations",
        "content": "Now it's your turn! Rotate through the sorting stations and figure out the rule for each one.",
        "image": "sorting_stations.jpg",
        "speakerNotes": {
          "objective": "Engage students in hands-on sorting activity.",
          "questions": [
            "What rule are you using to sort these objects?",
            "Can you explain your sorting rule to a partner?"
          ],
          "imagePrompt": "Image of different sorting stations with various objects to sort.",
          "scaffoldingTips": {
            "support": "Assist students in identifying the sorting rule.",
            "core": "Encourage students to explain their sorting rule to a partner.",
            "challenge": "Ask students to create their own sorting station."
          },
          "teacherTip": "Circulate the room to facilitate and monitor student discussions.",
          "aiPrompt": "NA"
        }
      },
      {
        "title": "📝 Reflect + Journal",
        "content": "Let's reflect on what we've learned. How does AI find patterns? What makes sorting tricky?",
        "image": "journal_task.jpg",
        "speakerNotes": {
          "objective": "Encourage reflection and consolidation of learning.",
          "questions": [
            "How does AI find patterns?",
            "What makes sorting tricky?"
          ],
          "imagePrompt": "Image of a student's journal with a drawing of a sorted object and a written rule.",
          "scaffoldingTips": {
            "support": "Provide sentence starters for reflection.",
            "core": "Encourage students to draw and write about their sorting rule.",
            "challenge": "Ask students to think of how they could teach a robot their sorting rule."
          },
          "teacherTip": "Encourage students to use specific vocabulary from the lesson in their reflections.",
          "aiPrompt": "NA"
        }
      },
      {
        "title": "🏁 Exit Task",
        "content": "For homework, find a pattern in your home and draw it. Can you explain the rule for your pattern?",
        "image": "exit_task.jpg",
        "speakerNotes": {
          "objective": "Extend learning beyond the classroom.",
          "questions": [
            "Can you find a pattern in your home?",
            "What's the rule for your pattern?"
          ],
          "imagePrompt": "Image of a child looking at patterns in wallpaper at home.",
          "scaffoldingTips": {
            "support": "Provide examples of patterns they might find at home.",
            "core": "Encourage students to draw and explain their pattern.",
            "challenge": "Ask students to find a pattern that would be difficult for a machine to recognize."
          },
          "teacherTip": "Remind students to bring their drawings to the next class for discussion.",
          "aiPrompt": "NA"
        }
      }
    ],
    "teacherMaterials": [
      {
        "name": "Lesson Plan & Objectives",
        "componentType": "lessonplanObjectives",
        "content": [
          {
            "sectionName": "Learning Objectives",
            "title": "What Students Will Learn",
            "description": "By the end of this lesson, students will:\n✅ Recognise what a pattern is (color, shape, size)\n✅ Explain how AI uses patterns to recognise and sort\n✅ Sort physical objects and describe patterns using key features\n✅ Reflect on why pattern recognition helps machines learn",
            "icon": "TargetIcon"
          }
        ],
        "lessonPlan": {
          "sections": [
            {
              "part": 1,
              "name": "Starter – What’s the Pattern?",
              "duration": 10,
              "activities": [
                {
                  "icon": "PlayCircleFill",
                  "primary": "Show a row of objects and ask: “What comes next?” (color sequence, shape order)",
                  "secondary": "Prompt: “What rules is your brain using?” Discussion: Humans see patterns easily – AI needs help to learn them"
                }
              ]
            },
            {
              "part": 2,
              "name": "Mila's Sorting Success!",
              "duration": 5,
              "activities": [
                {
                  "icon": "Robot",
                  "primary": "Story Hook: Mila is playing with her toy robot and teaches it to sort her blocks by color. The robot correctly puts red, blue, and yellow into separate trays.",
                  "secondary": "Prompt: “How did the robot know where to put each block?” Key Concept: Machines can follow clear visual rules to sort things using colors, shapes, or sizes."
                }
              ]
            },
            {
              "part": 3,
              "name": "Sorting Stations",
              "duration": 20,
              "activities": [
                {
                  "icon": "Sort",
                  "primary": "Students rotate through 3 stations",
                  "secondary": "Prompt: “What rule are you using?” Write or share rule with partner"
                }
              ]
            },
            {
              "part": 4,
              "name": "Reflect + Journal",
              "duration": 5,
              "activities": [
                {
                  "icon": "Book",
                  "primary": "Prompt: “How does AI find patterns? What makes sorting tricky?”",
                  "secondary": "Journal Task: “Draw one thing I sorted and write the rule I used."
                }
              ]
            }
          ]
        }
      },
      {
        "name": "Assessment & Homework",
        "componentType": "assessment",
        "content": [
          {
            "sectionName": "Assessment Criteria",
            "title": "What Students Should Be Able To Do",
            "description": "Students should be able to:\n✅ Define a pattern and name key features (color, shape, size)\n✅ Sort using a clear rule and explain their reasoning\n✅ Roleplay as an AI and follow classification rules\n✅ Reflect on how AI uses data to recognise patterns",
            "icon": "CheckIcon"
          },
          {
            "sectionName": "Homework Task",
            "title": "Journal Reflection",
            "description": "Draw one thing you sorted during the lesson and write about the rule you used to sort it. Reflect on how AI might use similar rules to recognize patterns.",
            "icon": "BookIcon"
          }
        ]
      },
      {
        "name": "Metacognitive Reflection",
        "componentType": "metacognitiveExercise",
        "content": [
          {
            "sectionName": "Reflection Prompts",
            "title": "Thinking About Thinking",
            "description": "“How do I decide what goes together?”\n“How might a machine get confused?”\n“Did I change my thinking to find a new rule?”",
            "icon": "ReflectionIcon"
          }
        ]
      },
      {
        "name": "Materials Needed",
        "componentType": "materials",
        "content": [
          {
            "sectionName": "Teacher Prep List",
            "title": "Materials for Today's Class",
            "description": "• Color blocks, shape cards, sorting trays\n• Station signs and rule sheets\n• Drawing materials",
            "icon": "ClipboardIcon"
          }
        ]
      }
    ]
  }
]